import React from 'react'
import classNames from 'classnames'
import { Icon } from '@zambezi/sdk/icons'

import { useStateContext } from '@zambezi/caballo-vivo'
import { formatDate } from '@apiEnabler/ob-common/lib/date-utils'
import { isAuthorizedPaymentStatus } from '@apiEnabler/ob-common/lib/payment-consents'
import DateTimeWarning from '@apiEnabler/ob-common/lib/view/DateTimeWarning'
import PaymentDetails from '@apiEnabler/ob-common/lib/view/stores/PaymentDetails'
import {
  getPaymentDetails,
  createMoneyFormatter,
} from '@apiEnabler/ob-common/lib/view/stores/get-details-from-state'
import HistoryCard from './HistoryCard'
import NavBar, { NavItem } from './NavBar'
import PageTitle from './PageTitle'

import style from '@apiEnabler/ob-common/lib/view/stores/Payment.module.css'

export default function Payment({ paymentId, brand }) {
  const state = useStateContext()
  const isStaffMember = state.get('isStaffMember')
  const serviceName = state.getIn(['brand', 'home'])
  const payment = state.getIn(['payments', 'index', paymentId])
  const paymentDetails = getPaymentDetails(payment)
  const moneyFormatter = createMoneyFormatter(paymentDetails)

  const renderedHistory = isStaffMember
    ? payment.get('history')
    : payment
        .get('history')
        .filter(record => !isAuthorizedPaymentStatus(record.status))

  const secondaryText = paymentDetails.isBalanceTransfer
    ? `from ${paymentDetails.recipientName}`
    : paymentDetails.individualPayments
    ? `to ${paymentDetails.individualPayments.length} payees`
    : `to ${paymentDetails.recipientName}`

  const activityLabel = formatActivityLabel(paymentDetails)
  const navItems = getNavItems(paymentDetails)

  return (
    <section className={`${style.content} js-payment`}>
      <NavBar items={navItems} padded />
      <header className={classNames(style.paymentHeader, 'js-header')}>
        <PageTitle
          consentType="payment"
          status={paymentDetails.status}
          label={moneyFormatter(paymentDetails.debitAmount)}
          secondaryText={secondaryText}
          showStatusIcon={!paymentDetails.isBulk}
        />
        <div className={classNames(style.dateLabel, 'js-date')}>
          {activityLabel}
        </div>
      </header>
      <div>
        <PaymentDetails
          payment={payment}
          brand={brand}
          isStaffMember={isStaffMember}
        />
        <div className={`${style.history} js-payment-history`}>
          {renderedHistory.size > 0 && (
            <h2 className={`${style.historyHeading} js-header`}>
              <span>History</span>
            </h2>
          )}
          {paymentDetails.isBulk && (
            <div className={style.annotation}>
              You’ll find more information on the status of individual payments
              in {serviceName} or with your service provider.
            </div>
          )}
          <div className="zb-card">
            {renderedHistory.map((record, i) => (
              <HistoryCard
                key={i}
                className={`js-history-card-${i}`}
                history={record}
                showIcon={!paymentDetails.isBulk}
                isBalanceTransfer={paymentDetails.isBalanceTransfer}
              />
            ))}
            {paymentDetails.isUpcoming && <CancelFDPMessage brand={brand} />}
          </div>
        </div>
      </div>
      <DateTimeWarning />
    </section>
  )
}

function CancelFDPMessage({ brand }) {
  return (
    <div className={style.historyFooter}>
      <div className={style.leftPaddedCard}>
        <span>
          You can cancel a {brand.get('paymentLabelScheduled')} in your{' '}
        </span>
        <a
          className={classNames(style.linkWithIcon, 'js-link')}
          href={brand.getIn(['links', 'Home'])}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`More information about ${brand.get(
            'home'
          )}, an external link that will open in a new tab`}
        >
          <span>{brand.get('home')}</span>
          <Icon name="popout-small" aria-hidden="true" />
        </a>
      </div>
    </div>
  )
}

function getActivityLabel({ isBulk, status, isBalanceTransfer }) {
  const activityLabels = {
    FAILED: isBulk ? 'submission failed' : 'failed',
    SUCCEEDED: isBulk
      ? 'submitted'
      : isBalanceTransfer
      ? 'balance transferred'
      : 'paid',
    CANCELLED: 'cancelled',
    DECLINED: 'declined',
    AUTHORIZED: 'confirmed',
    CONSUMED: 'confirmed',
    SUBMITTING: 'initiated',
    SUBMITTED: 'in progress',
    REJECTED: 'rejected',
  }

  return isBulk ? ` file ${activityLabels[status]}` : activityLabels[status]
}

function formatActivityLabel(paymentDetails) {
  const activity = paymentDetails.isUpcoming
    ? 'due'
    : getActivityLabel(paymentDetails)
  const date = paymentDetails.isUpcoming
    ? formatPaymentDate(paymentDetails.upcomingPaymentDate)
    : formatPaymentDate(paymentDetails.pastPaymentDate)

  return paymentDetails.isBalanceTransfer
    ? `${activity}`
    : `${activity} ${date}`
}

function formatPaymentDate(date) {
  return formatDate(date, 'DD MMMM YYYY')
}

function getNavItems(paymentDetails) {
  if (paymentDetails.isBulk) {
    return [
      new NavItem({ label: 'Bulk Payments', link: '/payments?type=bulk' }),
    ]
  } else if (paymentDetails.isInternational) {
    return [
      new NavItem({
        label: 'International Payments',
        link: '/payments?type=international',
      }),
    ]
  } else if (paymentDetails.isBalanceTransfer) {
    return [
      new NavItem({
        label: 'Balance Transfers',
        link: '/payments?type=transfer',
      }),
    ]
  } else {
    return [
      new NavItem({
        label: 'Domestic Payments',
        link: '/payments?type=domestic',
      }),
    ]
  }
}
